import type { ChangeEventHandler, HTMLProps } from 'react';

import { ChevronDown } from '@remarkable/ark-icons';
import { Label } from '@remarkable/ark-web';
import { useFormContext } from 'react-hook-form';

import type { CountryData } from 'src/utils/countryList';
import { COUNTRIES, useCountryLabel } from 'src/utils/countryList';
import { useFlagImage } from 'src/utils/useFlagImage';

import { Trans } from '@lingui/react/macro';
import { cn } from '../utils/classNamesHelper';

interface CountryPickerProps {
  onChange?: ChangeEventHandler<HTMLSelectElement>;
  isEditable?: boolean;
  name?: string;
  countries?: Array<CountryData>;
  disabled?: boolean;
  value?: string;
}

export const CountryPicker = ({
  className = '',
  onChange,
  isEditable = true,
  name = 'country',
  countries = COUNTRIES,
  disabled = false,
  value,
  ...rest
}: CountryPickerProps & Omit<HTMLProps<HTMLDivElement>, 'onChange'>) => {
  const { register, watch } = useFormContext();
  const formValue = value ?? (watch(name) as string);
  const displayValue = countries.find((c) => c.value === formValue)?.label;
  const flagImage = useFlagImage(formValue);
  const getCountryLabel = useCountryLabel();

  return (
    <div className={'w-full ' + className} {...rest}>
      <Label htmlFor="country">
        <Trans>Country</Trans>
      </Label>
      {isEditable ? (
        <div className={cn('rounded-sm relative flex items-center')}>
          {formValue ? (
            <img
              className="pointer-events-none absolute ml-12 size-32 rounded-full bg-neutral-light-4"
              src={flagImage.data}
              alt={`${displayValue ?? 'Country'} flag`}
            />
          ) : (
            <div className="absolute ml-12 size-32 rounded-full bg-neutral-light-4" />
          )}

          <select
            data-cy="country-picker"
            className={cn(
              'rounded-sm h-48 w-full cursor-pointer appearance-none border bg-none p-12 pl-56 shadow-border-muted',
              {
                'text-gray-500': !formValue,
                'cursor-not-allowed': disabled,
              }
            )}
            defaultValue=""
            id={name}
            {...register(name, {
              onChange,
              disabled,
              value: formValue,
            })}
            disabled={disabled}
            data-value={formValue}
          >
            <option value="" disabled hidden>
              <Trans>Select country</Trans>
            </option>
            {countries
              .sort((a, b) => a.label.localeCompare(b.label))
              .map((option) => (
                <option key={option.value} value={option.value}>
                  {getCountryLabel(option.value)}
                </option>
              ))}
          </select>
          <ChevronDown
            size={32}
            className="pointer-events-none absolute top-0 right-12 h-full text-pen-blue"
          />
        </div>
      ) : (
        <p className="min-h-[16px]">{displayValue ?? formValue ?? '-'}</p>
      )}
    </div>
  );
};
